/**
 * Scoping a tenant's payment ledger to one booking (COL-533).
 *
 * A tenant's `payments` rows are fetched by `user_id`, so a tenant with
 * successive tenancies gets every booking's rows back. Deriving deposit state
 * or "next payment" from that unscoped list is wrong in a way that is hard to
 * see: a previous tenancy's paid deposit satisfies the current booking's
 * requirement, and an old rent row can surface as this month's next payment.
 *
 * These predicates mirror V1's exactly (`Dashboard.tsx`, `depositMatchesBooking`
 * and the `type = "rent"` upcoming query) so the two dashboards — and the admin
 * side, which uses the same shape — can never disagree about what a tenant
 * owes.
 */

/** V1 matches on the exact value, not a substring. */
const DEPOSIT_TYPE = "deposit";
const RENT_TYPE = "rent";

function numericOrNull(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Whether a payment row belongs to this booking.
 *
 * `booking_id` wins; `metadata.booking_id` is the fallback for rows created
 * before the column was populated. A row carrying neither is treated as
 * belonging — the same permissive default V1 uses, because an unlinked legacy
 * deposit is still this tenant's money and excluding it would understate what
 * they have paid.
 */
export function paymentMatchesBooking(
  payment: {
    booking_id?: unknown;
    metadata?: { booking_id?: unknown } | null;
  } | null,
  bookingId: number | null
): boolean {
  if (!payment) return false;
  if (bookingId == null) return true;

  const linked = numericOrNull(payment.booking_id);
  if (linked !== null) return linked === bookingId;

  const meta = numericOrNull(payment.metadata?.booking_id);
  if (meta !== null) return meta === bookingId;

  return true;
}

export function isDepositPayment(payment: { type?: unknown } | null): boolean {
  return String(payment?.type ?? "") === DEPOSIT_TYPE;
}

export function isRentPayment(payment: { type?: unknown } | null): boolean {
  return String(payment?.type ?? "") === RENT_TYPE;
}

/** This booking's deposit rows, billed and paid. */
export function depositRowsForBooking<T extends { type?: unknown }>(
  payments: readonly T[],
  bookingId: number | null
): T[] {
  return payments.filter(
    (p) => isDepositPayment(p) && paymentMatchesBooking(p as any, bookingId)
  );
}

/**
 * This booking's rent rows.
 *
 * Filtering to rent before `selectEarliestUnpaidRentPayment` matters: that
 * selector suppresses any unpaid candidate whose month already has a paid row,
 * so a paid deposit sharing a month with unpaid rent would hide the rent and
 * the screen would read "nothing due".
 */
export function rentRowsForBooking<T extends { type?: unknown }>(
  payments: readonly T[],
  bookingId: number | null
): T[] {
  /*
    Unlinked rent rows BELONG, exactly as they do for deposits.

    This used to demand a non-null `booking_id`, on the stated grounds that V1
    does the same. V1 does not: its upcoming-rent query (`Dashboard.tsx`,
    `["payments-upcoming", user.id]`) filters on `user_id` and `type` only, with
    no booking predicate at all. So a tenant whose rent rows were written
    without the column — every row created before it was populated, and anything
    seeded by hand — had them all dropped here: `selectEarliestUnpaidRentPayment`
    saw an empty list, `rentOverdue` came back false, and the overview said
    "All done" to someone V1 was showing as months in arrears.

    `paymentMatchesBooking` is still STRICTER than V1 — a row linked to a
    different booking is excluded, which V1 never does — so successive tenancies
    stay separated. Only the "linked to nothing" case changes.
  */
  return payments.filter(
    (p) => isRentPayment(p) && paymentMatchesBooking(p as any, bookingId)
  );
}

export function bookingIdOf(booking: { id?: unknown } | null): number | null {
  return numericOrNull(booking?.id);
}

/**
 * Is a signature actually outstanding?
 *
 * NOT `isContractSentForSignature`, which stays true after signing — it means
 * "was ever released for signature", so using it as a next-action fact keeps
 * telling a signed tenant to sign and buries their overdue rent behind it.
 *
 * Mirrors V1's `isContractSigningStage` (Dashboard.tsx) and the definition in
 * `tenant-next-action.ts`: only these two contract states are waiting on the
 * tenant.
 */
const SIGNING_STAGES = ["envelope_draft", "ready_to_sign"] as const;

export function isContractAwaitingTenantSignature(
  booking: { contract_status?: unknown } | null
): boolean {
  const cs = String(booking?.contract_status ?? "").toLowerCase();
  return (SIGNING_STAGES as readonly string[]).includes(cs);
}
